/**
 * 场次元素类型枚举（与 DB `element.elementType` / `element_scene.elementType` 存储值一致）
 */
export enum ElementType {
  CAST = 'CAST',
  FEATURED = 'FEATURED',
  BACKGROUND = 'BACKGROUND',
  STUNT_PLAYERS = 'STUNT_PLAYERS',
  ANIMALS = 'ANIMALS',
  VEHICLES = 'VEHICLES',
  ART_PROPS = 'ART_PROPS',
  WARDROBE = 'WARDROBE',
  HAIR_MAKEUP = 'HAIR_MAKEUP',
  SFX_MU = 'SFX_MU',
  CAMERA = 'CAMERA',
  LIGHTING = 'LIGHTING',
  SFX = 'SFX',
  VFX = 'VFX',
  SOUND = 'SOUND',
  ADDL_LABOR = 'ADDL_LABOR',
  SPECIAL_EQUIPMENT = 'SPECIAL_EQUIPMENT',
  MISC = 'MISC',
  NOTE = 'NOTE'
}

/**
 * 场次元素数据状态枚举
 */
export enum ElementDataStatus {
  NORMAL = 'NORMAL',         // 正常
  DELETED = 'DELETED',       // 删除
  ARCHIVED = 'ARCHIVED'      // 归档
}

/** 受限日期单项：[日期, 备注]，日期格式 YYYY-MM-DD */
export type ElementRestrictedDateEntry = [string, string];

const RESTRICTED_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** 从 elementRestrictedDates 解析出日期字符串数组 */
export function getRestrictedDatesAsDateStrings(
  raw: ElementRestrictedDateEntry[] | undefined
): string[] {
  if (!raw?.length) return [];
  return raw.map(([d]) => d).filter((s) => RESTRICTED_DATE_REGEX.test(s));
}

/** 取某 element 在指定日期的受限备注（Note） */
export function getRestrictedDateNote(
  raw: ElementRestrictedDateEntry[] | undefined,
  dateStr: string
): string {
  if (!dateStr || !raw?.length) return '';
  const note = raw.find(([d]) => d === dateStr)?.[1];
  return typeof note === 'string' ? note.trim() : '';
}

/**
 * 场次元素信息类型
 */
export interface ElementInfo {
  elementID: string;
  elementBelongBreakdownID?: string;
  elementName?: string;
  elementType?: ElementType;
  elementAbbr?: string;
  elementSceneNumber?: number;
  elementQuantity?: number;
  elementCreatorID?: string;
  elementCreateTime?: string;
  elementUpdateTime?: string;
  elementDataStatus: ElementDataStatus;
  elementNO?: string;
  elementMaxQtyInBreakdown?: number;
  elementRestrictedDates?: ElementRestrictedDateEntry[];
}

/**
 * 查询场次元素参数类型
 */
export interface QueryElementParams {
  elementID?: string;
  elementBelongBreakdownID?: string;
  elementName?: string;
  elementType?: ElementType;
  elementQuantity?: number;
  elementCreatorID?: string;
  elementDataStatus?: ElementDataStatus;
}

/**
 * 创建场次元素参数类型
 */
export interface CreateElementParams {
  sceneID?: string;
  elementQtyInScene?: number;
  elementBelongBreakdownID?: string;
  elementName?: string;
  elementType?: ElementType;
  elementAbbr?: string;
  elementSceneNumber?: number;
  elementQuantity?: number;
  elementCreatorID?: string;
  elementDataStatus?: ElementDataStatus;
  elementNO?: string;
  elementMaxQtyInBreakdown?: number;
  elementRestrictedDates?: ElementRestrictedDateEntry[];
}

/**
 * 更新场次元素参数类型
 */
export interface UpdateElementParams {
  elementBelongBreakdownID?: string;
  elementName?: string;
  elementType?: ElementType;
  elementAbbr?: string;
  elementSceneNumber?: number;
  elementQuantity?: number;
  elementCreatorID?: string;
  elementDataStatus?: ElementDataStatus;
  elementNO?: string;
  elementMaxQtyInBreakdown?: number;
  elementRestrictedDates?: ElementRestrictedDateEntry[];
}

/**
 * 创建场次元素关系参数类型
 */
export interface CreateElementRelationParams {
  elementID: string;
  sceneID: string;
  elementQtyInScene?: number;
  elementBelongBreakdownID?: string;
}

/**
 * 删除场次元素关系参数类型
 */
export interface DeleteElementRelationParams {
  elementID: string;
  sceneID: string;
}

/**
 * 扩展场次元素信息，包含用户姓名和关系信息
 */
export interface ElementWithUserNames extends ElementInfo {
  elementCreatorName?: string;
  elementQtyInScene?: number;
  elementBelongBreakdownID?: string;
}

/**
 * Element与Scene的多对多关系类型
 */
export interface ElementSceneInfo {
  elementID: string;
  sceneID: string;
  elementQtyInScene?: number;
  elementType?: ElementType;
  elementName?: string;
  elementAbbr?: string;
  elementNO?: string;
  elementBelongBreakdownID?: string;
  createdAt?: Date | string;
}

/**
 * 场次元素类型标签映射（中文界面兜底）
 */
export const ElementTypeLabels: Record<ElementType, string> = {
  [ElementType.CAST]: '主要角色',
  [ElementType.FEATURED]: '特约角色',
  [ElementType.BACKGROUND]: '群众演员',
  [ElementType.STUNT_PLAYERS]: '动作演员',
  [ElementType.ANIMALS]: '动物',
  [ElementType.VEHICLES]: '车辆',
  [ElementType.ART_PROPS]: '美术道具',
  [ElementType.WARDROBE]: '服装',
  [ElementType.HAIR_MAKEUP]: '梳头化妆',
  [ElementType.SFX_MU]: '特效化妆',
  [ElementType.CAMERA]: '摄影',
  [ElementType.LIGHTING]: '灯光',
  [ElementType.SFX]: '现场特效',
  [ElementType.VFX]: '电脑特效',
  [ElementType.SOUND]: '收音',
  [ElementType.ADDL_LABOR]: '外增人员',
  [ElementType.SPECIAL_EQUIPMENT]: '特殊器材',
  [ElementType.MISC]: '其他',
  [ElementType.NOTE]: '备注'
};
